import { useEffect, useId, useRef, useState } from 'react';

import { Input } from '@/components/ui';
import { cn } from '@/lib/cn';

import { hasGoogleMaps } from './googleMaps';
import { createPlaceSearch, type PlaceSearchSession, type PlaceSuggestion } from './placeSearch';
import type { LatLng } from './mapProvider';

/**
 * "Where are we delivering?" as a search box.
 *
 * A customer who can type their building name should not have to drag a map
 * across a city to find it. The map stays — it is how the pin is placed on the
 * exact door — but this gets them to the right street in one go.
 *
 * DEBOUNCED, because each keystroke is a billed request. 300ms is the usual
 * compromise: long enough that typing a word is one call rather than seven,
 * short enough that it still feels live.
 *
 * It renders NOTHING if the app was built without a Maps key. The map and the
 * drag-the-pin flow work without one; losing the search bar is a smaller
 * failure than a search box that silently never returns anything.
 */

const DEBOUNCE_MS = 300;

export interface PlaceSearchProps {
  /** Where to bias results. The shop's own city, normally. */
  bias?: LatLng;
  /** The customer chose a place; move the map there and treat it as an answer. */
  onPicked: (coordinates: LatLng) => void;
  className?: string;
  /**
   * Floating over a map rather than sitting in a form: the label goes
   * visually-hidden (the box is self-evidently a search box there), the
   * hint about the map goes (the map is right behind it), and the box gets
   * a surface of its own so it reads against the tiles.
   */
  floating?: boolean;
}

export function PlaceSearch({ bias, onPicked, className, floating = false }: PlaceSearchProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const session = useRef<PlaceSearchSession | null>(null);

  // The session is created on first use rather than on mount: a customer who
  // drags the map and never types should not pull Google's SDK at all.
  async function ensureSession(): Promise<PlaceSearchSession | null> {
    if (session.current) return session.current;
    try {
      session.current = await createPlaceSearch(bias);
      return session.current;
    } catch {
      setFailed(true);
      return null;
    }
  }

  // Set when the query is changed BY US rather than typed.
  //
  // Choosing a suggestion writes the place's name into the box, and without
  // this that write looks exactly like typing: the effect below fires, asks
  // Google for suggestions matching the name just chosen, and reopens the
  // list the customer has only just dismissed. It also spends a billed
  // request to do it.
  const programmatic = useRef(false);

  useEffect(() => {
    if (programmatic.current) {
      programmatic.current = false;
      return;
    }
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        const active = await ensureSession();
        if (!active || cancelled) return;
        try {
          const found = await active.suggest(query);
          // A reply that arrived after the customer typed again is stale, and
          // showing it would make the list flicker back to an older word.
          if (!cancelled) {
            setSuggestions(found);
            setOpen(found.length > 0);
          }
        } catch {
          if (!cancelled) setSuggestions([]);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Only `query`. `ensureSession` is stable in effect — it memoises into a
    // ref — and `bias` is read once when the session is created, because it
    // is the shop's own city and does not move. Listing either would re-run
    // this on every render and re-fire a billed request per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function choose(suggestion: PlaceSuggestion) {
    const active = await ensureSession();
    if (!active) return;
    setOpen(false);
    // Cleared as well as closed: `onFocus` reopens the list if there is
    // anything in it, so leaving the old results behind means the list
    // springs back the moment the customer clicks into the box again.
    setSuggestions([]);
    programmatic.current = true;
    setQuery(`${suggestion.primary}${suggestion.secondary ? `, ${suggestion.secondary}` : ''}`);
    const coordinates = await active.resolve(suggestion.id);
    if (coordinates) onPicked(coordinates);
  }

  // Nothing at all without a key — decided up front, not on the first
  // keystroke. A search box that can never answer is a worse failure than
  // no search box, and floating over the map it also sat on top of
  // Leaflet's zoom control, which is the map the keyless build draws.
  if (failed || !hasGoogleMaps()) return null;

  return (
    <div className={cn('relative', className)}>
      <Input
        label="Search for your area or building"
        labelHidden={floating}
        placeholder={floating ? 'Search for your area or building' : undefined}
        className={floating ? 'rounded-control bg-surface shadow-lg' : undefined}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setOpen(suggestions.length > 0)}
        // Blur is delayed: a click on a suggestion blurs the input first, and
        // closing immediately would unmount the thing being clicked.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        hint={floating ? undefined : 'Or move the map below to your door.'}
      />

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-control border border-line bg-surface shadow-lg"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                // `onMouseDown`, not `onClick`: the input's blur fires first
                // and would close the list before a click ever landed.
                onMouseDown={(event) => {
                  event.preventDefault();
                  void choose(suggestion);
                }}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none"
              >
                <span className="text-sm text-ink">{suggestion.primary}</span>
                {suggestion.secondary ? (
                  <span className="text-xs text-ink-subtle">{suggestion.secondary}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
